import { AnimatePresence, motion } from 'framer-motion'

import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useNavigate, useSearchParams } from 'react-router-dom'

import apiClient from '@/api/axios'
import { API_ENDPOINTS } from '@/api/endpoints'

import { useAppSettings } from '@/stores/useAppSettings'

import { paths } from '@/config/paths'

import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from '@/components/ui/stepper'

import { SystemMessage } from '@/components/errors/SystemMessage'

import { AccountForm } from '@/components/auth/AccountForm'
import { BranchSelector } from '@/components/auth/BranchSelector'
import { CheckoutForm } from '@/components/auth/CheckoutForm'
import { PlanSelector } from '@/components/auth/PlanSelector'

import LoadingSpinner from '../components/LoadingSpinner'
import { AppFooterLinks } from '../layouts/components/AppFooterLinks'
import { AppLogo } from '../layouts/components/AppLogo'

const getSteps = (t: any) => [
  { step: 1, title: t('auth:registerPage.steps.selectBranch', 'Select Branch') },
  { step: 2, title: t('auth:registerPage.steps.choosePlan', 'Choose Plan') },
  { step: 3, title: t('auth:registerPage.steps.createAccount', 'Create Account') },
  { step: 4, title: t('auth:registerPage.steps.makePayment', 'Make Payment') },
]

export function RegisterPage() {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { getSetting } = useAppSettings()
  const [initialized, setInitialized] = useState(false)
  const [step, setStep] = useState(1)
  const steps = getSteps(t)
  const [pendingToken, setPendingToken] = useState<string | null>(null)

  const [selectedBranch, setSelectedBranch] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [checkoutData, setCheckoutData] = useState<any>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isRegistrationDisabled = getSetting('security.disable_registration', false)
  const isGymClosed = getSetting('operations.gym_closed', false)

  // On mount check URL for step=4 & session_id
  useEffect(() => {
    const urlStep = Number(searchParams.get('step')) || 1
    const session_id = searchParams.get('session_id')

    if (urlStep === 4 && session_id) {
      setPendingToken(session_id)

      // Fetch pending registration data by session_id to fill form state
      ;(async () => {
        try {
          const params = `session_id=${session_id}`
          const res = await apiClient.get(API_ENDPOINTS.register.checkout(params))
          const data = res?.data?.data
          setCheckoutData(data)
          setStep(4)
        } catch (error: any) {
          if (error.response?.status === 422) {
            setErrorMessage(error.response?.data.errorMessage)
          } else {
            setErrorMessage(
              t('registerPage.errors.default', 'An error occurred while loading your registration. Please try again.')
            )
          }
        } finally {
          setInitialized(true)
        }
      })()
    } else {
      setInitialized(true)
    }
  }, [searchParams, navigate])

  const nextStep = () => setStep((s) => Math.min(s + 1, steps.length))
  const prevStep = () => setStep((s) => Math.max(s - 1, 1))

  if (!initialized) {
    return <LoadingSpinner />
  }

  if (isGymClosed) {
    return (
      <div className='flex min-h-screen w-full flex-col items-center justify-center p-6'>
        <SystemMessage
          variant='error'
          title={t('registerPage.gymClosed.title', 'Gym Temporarily Closed')}
          message={t(
            'registerPage.gymClosed.message',
            'New member registration is currently unavailable as the gym is temporarily closed. Please check back later or contact support for more information.'
          )}
          action={{
            label: t('registerPage.gymClosed.loginButton', 'Go to Login'),
            onClick: () => navigate(paths.public.auth.login.getHref()),
          }}
          secondaryAction={{
            label: t('registerPage.registrationDisabled.returnHome', 'Return to Home'),
            onClick: () => navigate(paths.public.home.getHref()),
          }}
        />
      </div>
    )
  }

  if (isRegistrationDisabled) {
    return (
      <div className='flex min-h-screen w-full flex-col items-center justify-center p-6'>
        <SystemMessage
          variant='warning'
          title={t('registerPage.registrationDisabled.title', 'Registration Temporarily Disabled')}
          message={t(
            'registerPage.registrationDisabled.message',
            'New user registration is currently disabled. Please contact support for assistance or try again later.'
          )}
          action={{
            label: t('registerPage.registrationDisabled.goToLogin', 'Go to Login'),
            onClick: () => navigate(paths.public.auth.login.getHref()),
          }}
          secondaryAction={{
            label: t('registerPage.registrationDisabled.returnHome', 'Return to Home'),
            onClick: () => navigate(paths.public.home.getHref()),
          }}
        />
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className='flex min-h-screen w-full flex-col items-center justify-center p-6'>
        <SystemMessage
          variant='error'
          title={t('registerPage.errorMessage.title', 'Unable to Proceed')}
          message={errorMessage}
          action={{
            label: t('auth:registerPage.errorMessage.returnHome', 'Return to Home'),
            onClick: () => navigate(paths.public.home.getHref()),
          }}
        />
      </div>
    )
  }

  return (
    <div className='bg-muted/60 flex min-h-screen w-full flex-col items-center justify-between space-y-6 px-4 py-6'>
      {/* Logo */}
      <AppLogo />
      <div className='flex h-full min-h-screen w-full max-w-4xl flex-col gap-8 p-1 py-4 md:p-6 md:py-8'>
        {/* Fixed Stepper */}
        <div className='rounded-xl bg-white p-4 pt-6 shadow'>
          <Stepper value={step} orientation='horizontal'>
            {steps.map(({ step: s, title }) => (
              <StepperItem key={s} step={s} className='relative flex-1 flex-col!'>
                <StepperTrigger className='flex-col gap-3 rounded transition-transform duration-300'>
                  <StepperIndicator />
                  <div className='space-y-0.5 px-2'>
                    <StepperTitle>{title}</StepperTitle>
                  </div>
                </StepperTrigger>
                {s < steps.length && (
                  <StepperSeparator className='absolute inset-x-0 top-3 left-[calc(50%+0.75rem+0.125rem)] -order-1 m-0 -translate-y-1/2 group-data-[orientation=horizontal]/stepper:w-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=horizontal]/stepper:flex-none rtl:right-[calc(50%+0.75rem+0.125rem)]' />
                )}
              </StepperItem>
            ))}
          </Stepper>
        </div>

        {/* Step content with transitions */}
        <div className='flex-1'>
          <AnimatePresence mode='wait' initial={false}>
            {step === 1 && (
              <motion.div
                key='step-11'
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <BranchSelector
                  selectedBranch={selectedBranch}
                  onSelect={(id) => setSelectedBranch(id)}
                  onNext={() => {
                    if (selectedBranch) nextStep()
                    else alert(t('auth:registerPage.alerts.selectBranch', 'Please select a branch.'))
                  }}
                />
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key='step-22'
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <PlanSelector
                  selectedBranch={selectedBranch}
                  selectedPlan={selectedPlan}
                  onSelect={(id) => setSelectedPlan(id)}
                  onNext={() => {
                    if (selectedPlan) nextStep()
                  }}
                  onBack={() => prevStep()}
                />
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key='step-33'
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <AccountForm
                  branchId={selectedBranch}
                  planId={selectedPlan}
                  onSuccess={() => {
                    navigate(paths.public.auth.verificationStatus.getHref())
                  }}
                  onBack={() => prevStep()}
                />
              </motion.div>
            )}

            {step === 4 && checkoutData && (
              <motion.div
                key='step-44'
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <CheckoutForm data={checkoutData} sessionId={pendingToken} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* Footer Links */}
      <AppFooterLinks />
    </div>
  )
}
